// src/features/role-profiles/components/RoleProfilePermissionsEditor.tsx

import React, { useState, useEffect, useMemo } from 'react';
import { Card, Checkbox, Space, Divider, Typography, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { RolePermissions, normalizeRolePermissions } from '../../../types/user';
import { Business, BusinessFeatureKey, isFeatureEnabled } from '../../../types/business';
import { useAppTranslation } from '../../../hooks/useAppTranslation';

const { Title, Text } = Typography;

interface PermissionItem {
  key: keyof RolePermissions;
  label: string;
  description?: string;
  dependsOn?: keyof RolePermissions;
}

interface PermissionGroup {
  title: string;
  description?: string;
  /** Clé i18n `roleProfiles.groups.<groupId>.*` si définie. */
  groupId?: string;
  featureKey?: BusinessFeatureKey;
  permissions: PermissionItem[];
}

interface PermissionSection {
  sectionId: string;
  /** Libellé identique à l’entrée du menu */
  sectionTitle: string;
  description?: string;
  featureKey?: BusinessFeatureKey;
  groups: PermissionGroup[];
}

// Structure calquée sur le menu (GenericLayout) : une section = un bloc menu, un groupe = une entrée/sous-entrée
/**
 * Features qui conditionnent un bloc de permissions.
 * - featureKey du groupe = feature métier du bloc
 * - featureKey de la section = parent (ex. Caisse) aussi requis s’il est défini
 */
export const getPermissionGroupGatingFeatures = (
  section: Pick<PermissionSection, 'featureKey'>,
  group: Pick<PermissionGroup, 'featureKey'>,
): BusinessFeatureKey[] => {
  const keys = new Set<BusinessFeatureKey>();
  if (section.featureKey) keys.add(section.featureKey);
  if (group.featureKey) keys.add(group.featureKey);
  return Array.from(keys);
};

const PERMISSION_SECTIONS: PermissionSection[] = [
  {
    sectionId: 'transactions',
    sectionTitle: '🛒 Transactions',
    featureKey: 'pos',
    groups: [
      {
        title: 'Prestations',
        /** Vente de prestations en caisse : nécessite Caisse (pos) + catalogue Prestations (services). */
        featureKey: 'services',
        permissions: [
          { key: 'canViewPrestations', label: 'Voir les prestations' },
          { key: 'canCreatePrestations', label: 'Créer des prestations', dependsOn: 'canViewPrestations' },
          { key: 'canUpdatePrestations', label: 'Modifier des prestations', dependsOn: 'canViewPrestations' },
          { key: 'canDeletePrestations', label: 'Supprimer des prestations', dependsOn: 'canViewPrestations' },
        ],
      },
      {
        title: 'Fabrication',
        description: "Ordres de fabrication et consommation réelle de stock",
        featureKey: 'manufacturing',
        permissions: [
          { key: 'canViewManufacturingOrders', label: 'Voir les ordres' },
          { key: 'canCreateManufacturingOrders', label: 'Créer des ordres', dependsOn: 'canViewManufacturingOrders' },
          { key: 'canUpdateManufacturingOrders', label: 'Modifier des ordres', dependsOn: 'canViewManufacturingOrders' },
          { key: 'canCloseManufacturingOrders', label: 'Clôturer des ordres (appliquer stock)', dependsOn: 'canViewManufacturingOrders' },
          { key: 'canDeleteManufacturingOrders', label: 'Supprimer des ordres', dependsOn: 'canViewManufacturingOrders' },
        ],
      },
      {
        title: 'Ventes de produits (Caisse)',
        description: 'Historique Produits, Nouvelle Transaction produits',
        featureKey: 'inventory',
        permissions: [
          { key: 'canViewProductSales', label: 'Voir les ventes' },
          { key: 'canCreateProductSales', label: 'Créer des ventes', dependsOn: 'canViewProductSales' },
          { key: 'canUpdateProductSales', label: 'Modifier des ventes', dependsOn: 'canViewProductSales' },
          { key: 'canDeleteProductSales', label: 'Supprimer des ventes', dependsOn: 'canViewProductSales' },
        ],
      },
      {
        title: 'Rapports de caisse',
        description: 'Menu Transactions > Rapports',
        featureKey: 'pos',
        permissions: [
          { key: 'canViewCashReports', label: 'Voir les rapports de caisse' },
          { key: 'canViewAdvancedCashReports', label: 'Rapports avancés (PDF filtrés)', dependsOn: 'canViewCashReports' },
        ],
      },
      {
        groupId: 'saleCorrection',
        title: 'Correction de ventes',
        description: 'Annuler, corriger ou rembourser des articles d’une transaction (Caisse)',
        featureKey: 'pos',
        permissions: [
          { key: 'canVoidSales', label: 'Annuler une vente' },
          { key: 'canCorrectSales', label: 'Corriger une vente' },
          { key: 'canIssueSaleCredits', label: 'Rembourser des articles d’une vente' },
        ],
      },
    ],
  },
  {
    sectionId: 'clients',
    sectionTitle: '👥 Clients',
    groups: [
      {
        title: 'Clients',
        featureKey: 'clients',
        permissions: [
          { key: 'canViewClients', label: 'Voir les clients' },
          { key: 'canCreateClients', label: 'Créer des clients', dependsOn: 'canViewClients' },
          { key: 'canUpdateClients', label: 'Modifier des clients', dependsOn: 'canViewClients' },
          { key: 'canDeleteClients', label: 'Supprimer des clients', dependsOn: 'canViewClients' },
        ],
      },
      {
        title: 'Paiement en plusieurs',
        featureKey: 'paymentPlans',
        description: 'Acomptes, restes à payer et versements',
        permissions: [
          { key: 'canViewPaymentPlans', label: 'Voir les paiements en plusieurs' },
          { key: 'canCreatePaymentPlans', label: 'Lancer un paiement en plusieurs', dependsOn: 'canViewPaymentPlans' },
          { key: 'canUpdatePaymentPlans', label: 'Modifier des paiements en plusieurs', dependsOn: 'canViewPaymentPlans' },
          { key: 'canCancelPaymentPlans', label: 'Annuler des paiements en plusieurs', dependsOn: 'canViewPaymentPlans' },
          { key: 'canCompletePaymentPlans', label: 'Clôturer des paiements en plusieurs (soldés)', dependsOn: 'canViewPaymentPlans' },
          { key: 'canAddPaymentPlanPayments', label: 'Enregistrer des versements', dependsOn: 'canViewPaymentPlans' },
          { key: 'canVoidPaymentPlanPayments', label: 'Annuler un versement', dependsOn: 'canViewPaymentPlans' },
          { key: 'canCorrectPaymentPlanPayments', label: 'Corriger un versement', dependsOn: 'canViewPaymentPlans' },
        ],
      },
      {
        title: 'Fidélité carte',
        featureKey: 'loyaltyPass',
        description: 'Carte Pass (tampons / récompenses) — mutex avec la cagnotte',
        permissions: [
          { key: 'canViewLoyaltyPass', label: 'Voir la fidélité carte' },
          {
            key: 'canManageLoyaltyPass',
            label: 'Paramétrer le programme carte',
            dependsOn: 'canViewLoyaltyPass',
          },
        ],
      },
      {
        title: 'Packs catalogue',
        featureKey: 'catalogPacks',
        description: 'Forfaits prestations/produits à prix fixe',
        permissions: [
          { key: 'canViewCatalogPacks', label: 'Voir les packs' },
          {
            key: 'canManageCatalogPacks',
            label: 'Créer et modifier les packs',
            dependsOn: 'canViewCatalogPacks',
          },
        ],
      },
    ],
  },
  {
    sectionId: 'referents',
    sectionTitle: '👨‍⚕️ Référents',
    featureKey: 'referents',
    groups: [
      {
        title: 'Référents',
        featureKey: 'referents',
        permissions: [
          { key: 'canViewReferents', label: 'Voir les référents' },
          { key: 'canCreateReferents', label: 'Créer des référents', dependsOn: 'canViewReferents' },
          { key: 'canUpdateReferents', label: 'Modifier des référents', dependsOn: 'canViewReferents' },
          { key: 'canDeleteReferents', label: 'Supprimer des référents', dependsOn: 'canViewReferents' },
        ],
      },
    ],
  },
  {
    sectionId: 'dashboard',
    sectionTitle: '📊 Tableau de bord',
    featureKey: 'dashboard',
    groups: [
      {
        title: 'Tableau de bord',
        featureKey: 'dashboard',
        permissions: [{ key: 'canViewDashboard', label: 'Voir le tableau de bord' }],
      },
    ],
  },
  {
    sectionId: 'appointments',
    sectionTitle: '📅 Rendez-vous',
    featureKey: 'appointments',
    groups: [
      {
        title: 'Rendez-vous',
        featureKey: 'appointments',
        permissions: [
          { key: 'canViewAppointments', label: 'Voir les rendez-vous (calendrier)' },
          { key: 'canCreateAppointments', label: 'Créer des rendez-vous', dependsOn: 'canViewAppointments' },
          { key: 'canUpdateAppointments', label: 'Modifier des rendez-vous', dependsOn: 'canViewAppointments' },
          { key: 'canDeleteAppointments', label: 'Supprimer des rendez-vous', dependsOn: 'canViewAppointments' },
        ],
      },
      {
        title: 'Demandes de Rendez-vous',
        featureKey: 'appointments',
        permissions: [
          { key: 'canViewAppointmentRequests', label: 'Voir les demandes de RDV' },
        ],
      },
    ],
  },
  {
    sectionId: 'pickup',
    sectionTitle: '🧺 Ramassage',
    featureKey: 'pickup',
    groups: [
      {
        title: 'Demandes de ramassage',
        featureKey: 'pickup',
        permissions: [
          { key: 'canViewPickups', label: 'Voir les demandes de ramassage' },
          {
            key: 'canManagePickups',
            label: 'Configurer le module (lien, zones, forfait)',
            dependsOn: 'canViewPickups',
          },
          {
            key: 'canUpdatePickupStatus',
            label: 'Changer le statut des demandes',
            dependsOn: 'canViewPickups',
          },
          {
            key: 'canValidatePickup',
            label: 'Valider (= enregistrer la vente / forfait ramassage)',
            dependsOn: 'canViewPickups',
          },
        ],
      },
    ],
  },
  {
    sectionId: 'stock',
    sectionTitle: '📦 Stock',
    featureKey: 'inventory',
    description: 'Chaque entrée du menu Stock = un rôle distinct (Produits, Catégories, Mouvements, etc.)',
    groups: [
      {
        title: 'Produits',
        featureKey: 'inventory',
        permissions: [
          { key: 'canViewProducts', label: 'Voir les produits' },
          { key: 'canCreateProducts', label: 'Créer des produits', dependsOn: 'canViewProducts' },
          { key: 'canUpdateProducts', label: 'Modifier des produits', dependsOn: 'canViewProducts' },
          { key: 'canDeleteProducts', label: 'Supprimer des produits', dependsOn: 'canViewProducts' },
        ],
      },
      {
        title: 'Catégories de produits',
        featureKey: 'inventory',
        permissions: [
          { key: 'canViewCategories', label: 'Voir les catégories' },
          { key: 'canCreateCategories', label: 'Créer des catégories', dependsOn: 'canViewCategories' },
          { key: 'canUpdateCategories', label: 'Modifier des catégories', dependsOn: 'canViewCategories' },
          { key: 'canDeleteCategories', label: 'Supprimer des catégories', dependsOn: 'canViewCategories' },
        ],
      },
      {
        title: 'Mouvements de stock',
        featureKey: 'inventory',
        permissions: [
          { key: 'canViewStockMovements', label: 'Voir les mouvements de stock' },
          { key: 'canCreateStockMovements', label: 'Créer des mouvements', dependsOn: 'canViewStockMovements' },
          { key: 'canUpdateStockMovements', label: 'Modifier des mouvements', dependsOn: 'canViewStockMovements' },
          { key: 'canDeleteStockMovements', label: 'Supprimer des mouvements', dependsOn: 'canViewStockMovements' },
        ],
      },
      {
        title: 'Pertes',
        featureKey: 'inventoryLosses',
        permissions: [
          { key: 'canViewInventoryLosses', label: 'Voir les pertes' },
          { key: 'canCreateInventoryLosses', label: 'Déclarer des pertes', dependsOn: 'canViewInventoryLosses' },
        ],
      },
      {
        title: 'Bons d\'envoi externes',
        featureKey: 'inventoryExternalDispatch',
        permissions: [
          { key: 'canViewExternalDispatches', label: 'Voir les bons d\'envoi externes' },
          { key: 'canCreateExternalDispatches', label: 'Créer des bons d\'envoi externes', dependsOn: 'canViewExternalDispatches' },
          { key: 'canUpdateExternalDispatches', label: 'Modifier des bons d\'envoi externes', dependsOn: 'canViewExternalDispatches' },
          { key: 'canDeleteExternalDispatches', label: 'Supprimer des bons d\'envoi externes', dependsOn: 'canViewExternalDispatches' },
        ],
      },
      {
        title: 'Destinations externes',
        featureKey: 'inventoryExternalDispatch',
        permissions: [
          { key: 'canViewExternalDestinations', label: 'Voir les destinations externes' },
          { key: 'canCreateExternalDestinations', label: 'Créer des destinations externes', dependsOn: 'canViewExternalDestinations' },
          { key: 'canUpdateExternalDestinations', label: 'Modifier des destinations externes', dependsOn: 'canViewExternalDestinations' },
          { key: 'canDeleteExternalDestinations', label: 'Désactiver/Réactiver des destinations externes', dependsOn: 'canViewExternalDestinations' },
        ],
      },
      {
        title: 'Inventaires physiques',
        featureKey: 'inventory',
        permissions: [
          { key: 'canViewInventoryAudit', label: 'Voir les inventaires physiques' },
          { key: 'canCreateInventoryAudit', label: 'Créer des inventaires', dependsOn: 'canViewInventoryAudit' },
          { key: 'canUpdateInventoryAudit', label: 'Modifier des inventaires', dependsOn: 'canViewInventoryAudit' },
          { key: 'canDeleteInventoryAudit', label: 'Supprimer des inventaires', dependsOn: 'canViewInventoryAudit' },
          { key: 'canCountInventory', label: 'Effectuer les comptages', dependsOn: 'canViewInventoryAudit' },
          { key: 'canValidateInventory', label: 'Valider les inventaires', dependsOn: 'canViewInventoryAudit' },
          { key: 'canViewInventoryAuditReports', label: 'Voir les rapports d\'inventaires', dependsOn: 'canViewInventoryAudit' },
        ],
      },
      {
        title: 'Rapports Stock',
        description: 'Menu Stock > Rapports Stock',
        featureKey: 'inventory',
        permissions: [{ key: 'canViewInventoryReports', label: 'Voir les rapports d\'inventaire' }],
      },
    ],
  },
  {
    sectionId: 'purchasesExpenses',
    sectionTitle: '🚚 Achats & Dépenses',
    featureKey: 'purchasesExpenses',
    groups: [
      {
        title: 'Fournisseurs',
        featureKey: 'purchasesExpenses',
        permissions: [
          { key: 'canViewSuppliers', label: 'Voir les fournisseurs' },
          { key: 'canCreateSuppliers', label: 'Créer des fournisseurs', dependsOn: 'canViewSuppliers' },
          { key: 'canUpdateSuppliers', label: 'Modifier des fournisseurs', dependsOn: 'canViewSuppliers' },
          { key: 'canDeleteSuppliers', label: 'Supprimer des fournisseurs', dependsOn: 'canViewSuppliers' },
          { key: 'canViewSupplierReports', label: 'Voir les rapports fournisseurs', dependsOn: 'canViewSuppliers' },
        ],
      },
      {
        title: 'Approvisionnements',
        featureKey: 'purchasesExpenses',
        permissions: [
          { key: 'canViewDeliveries', label: 'Voir les approvisionnements', dependsOn: 'canViewSuppliers' },
          { key: 'canCreateDeliveries', label: 'Créer des approvisionnements', dependsOn: 'canViewDeliveries' },
          { key: 'canUpdateDeliveries', label: 'Modifier des approvisionnements', dependsOn: 'canViewDeliveries' },
          { key: 'canDeleteDeliveries', label: 'Supprimer des approvisionnements', dependsOn: 'canViewDeliveries' },
        ],
      },
      {
        title: 'Dépenses',
        description: 'Contrôle de visibilité des dépenses sensibles',
        featureKey: 'purchasesExpenses',
        permissions: [
          { key: 'canViewExpenses', label: 'Voir les dépenses' },
          { key: 'canViewLargeExpenses', label: 'Voir les grosses dépenses (sensibles)', dependsOn: 'canViewExpenses' },
          { key: 'canCreateExpenses', label: 'Créer des dépenses', dependsOn: 'canViewExpenses' },
          { key: 'canUpdateExpenses', label: 'Modifier des dépenses', dependsOn: 'canViewExpenses' },
          { key: 'canDeleteExpenses', label: 'Supprimer des dépenses', dependsOn: 'canViewExpenses' },
        ],
      },
      {
        title: 'Types de dépenses',
        featureKey: 'expenseCategories',
        description: 'Paramètres → Types de dépenses',
        permissions: [
          { key: 'canViewExpenseCategories', label: 'Voir les types de dépenses' },
          {
            key: 'canCreateExpenseCategories',
            label: 'Créer des types de dépenses',
            dependsOn: 'canViewExpenseCategories',
          },
          {
            key: 'canUpdateExpenseCategories',
            label: 'Modifier des types de dépenses',
            dependsOn: 'canViewExpenseCategories',
          },
          {
            key: 'canDeleteExpenseCategories',
            label: 'Supprimer des types de dépenses',
            dependsOn: 'canViewExpenseCategories',
          },
        ],
      },
      {
        title: 'Notes de frais',
        description: 'Soumission par employé + validation (dépense + remboursement paie ; sans double comptage en Dépenses)',
        featureKey: 'expenseNotes',
        permissions: [
          { key: 'canViewExpenseNotes', label: 'Voir les notes de frais' },
          { key: 'canCreateExpenseNotes', label: 'Créer des notes de frais', dependsOn: 'canViewExpenseNotes' },
          { key: 'canViewAllExpenseNotes', label: 'Voir les notes de frais de tout le monde', dependsOn: 'canViewExpenseNotes' },
          { key: 'canApproveExpenseNotes', label: 'Valider/rejeter les notes de frais', dependsOn: 'canViewExpenseNotes' },
        ],
      },
    ],
  },
  {
    sectionId: 'accounting',
    sectionTitle: '🧮 Comptabilité',
    featureKey: 'accounting',
    groups: [
      {
        title: 'Comptabilité',
        description: 'Tableau de bord, Journaux, Déclarations TVA',
        featureKey: 'accounting',
        permissions: [{ key: 'canViewAccounting', label: 'Voir la comptabilité' }],
      },
    ],
  },
  {
    sectionId: 'marketing',
    sectionTitle: '🎁 Marketing',
    featureKey: 'marketing',
    groups: [
      {
        title: 'Marketing',
        featureKey: 'marketing',
        permissions: [
          { key: 'canViewMarketing', label: 'Voir le marketing' },
          { key: 'canCreateMarketing', label: 'Créer des campagnes', dependsOn: 'canViewMarketing' },
          { key: 'canUpdateMarketing', label: 'Modifier des campagnes', dependsOn: 'canViewMarketing' },
          { key: 'canDeleteMarketing', label: 'Supprimer des campagnes', dependsOn: 'canViewMarketing' },
        ],
      },
    ],
  },
  {
    sectionId: 'messagesClients',
    sectionTitle: '💬 Messages Clients',
    featureKey: 'messagesClients',
    description: 'Messages post-transaction (délai business configurable, envoi WhatsApp manuel).',
    groups: [
      {
        title: 'Messages Clients',
        featureKey: 'messagesClients',
        permissions: [
          { key: 'canViewMessagesClients', label: 'Voir les messages clients' },
          { key: 'canSendMessagesClients', label: 'Envoyer les messages clients', dependsOn: 'canViewMessagesClients' },
          { key: 'canUpdateMessagesClients', label: 'Mettre à jour le statut des messages', dependsOn: 'canViewMessagesClients' },
          {
            key: 'canManageMessagesClientsSettings',
            label: 'Gérer les paramètres Messages Clients',
            dependsOn: 'canViewMessagesClients',
          },
        ],
      },
    ],
  },
  {
    sectionId: 'services',
    sectionTitle: '🔧 Prestations',
    featureKey: 'services',
    groups: [
      {
        title: 'Catalogue prestations',
        featureKey: 'services',
        permissions: [
          { key: 'canViewServices', label: 'Voir les prestations' },
          { key: 'canCreateServices', label: 'Créer des prestations', dependsOn: 'canViewServices' },
          { key: 'canUpdateServices', label: 'Modifier des prestations', dependsOn: 'canViewServices' },
          { key: 'canDeleteServices', label: 'Supprimer des prestations', dependsOn: 'canViewServices' },
        ],
      },
    ],
  },
  {
    sectionId: 'hr',
    sectionTitle: '👥 Employés et paies',
    featureKey: 'employeesPayroll',
    groups: [
      {
        title: 'Employés',
        featureKey: 'employeesPayroll',
        permissions: [
          { key: 'canViewEmployees', label: 'Voir les employés' },
          { key: 'canCreateEmployees', label: 'Créer des employés', dependsOn: 'canViewEmployees' },
          { key: 'canUpdateEmployees', label: 'Modifier des employés', dependsOn: 'canViewEmployees' },
          { key: 'canDeleteEmployees', label: 'Supprimer des employés', dependsOn: 'canViewEmployees' },
        ],
      },
      {
        title: 'Fiches de paie',
        featureKey: 'employeesPayroll',
        permissions: [
          { key: 'canViewPayrolls', label: 'Voir les fiches de paie' },
          { key: 'canCreatePayrolls', label: 'Créer/générer des fiches de paie', dependsOn: 'canViewPayrolls' },
          { key: 'canUpdatePayrolls', label: 'Modifier/valider des fiches de paie', dependsOn: 'canViewPayrolls' },
          { key: 'canDeletePayrolls', label: 'Supprimer des fiches de paie', dependsOn: 'canViewPayrolls' },
        ],
      },
      {
        title: 'Ajustements salariaux',
        featureKey: 'employeesPayroll',
        permissions: [
          { key: 'canViewSalaryAdjustments', label: 'Voir les ajustements salariaux', dependsOn: 'canViewPayrolls' },
          { key: 'canCreateSalaryAdjustments', label: 'Créer des ajustements', dependsOn: 'canViewSalaryAdjustments' },
          { key: 'canUpdateSalaryAdjustments', label: 'Modifier des ajustements', dependsOn: 'canViewSalaryAdjustments' },
          { key: 'canApproveSalaryAdjustments', label: 'Approuver/rejeter les ajustements', dependsOn: 'canViewSalaryAdjustments' },
        ],
      },
    ],
  },
  // Feature autonome (pas de FEATURE_REQUIRES_OF vers employeesPayroll) — ne pas imbriquer
  // sous la section paie sinon le gate exige les deux features.
  {
    sectionId: 'performanceIndicators',
    sectionTitle: '📊 Indicateur de performance',
    featureKey: 'performanceIndicators',
    groups: [
      {
        title: 'Indicateur de performance',
        description: 'Objectifs mensuels + validation (crée une prime variable dans les ajustements si Paie active)',
        featureKey: 'performanceIndicators',
        permissions: [
          { key: 'canViewPerformanceObjectives', label: 'Voir les objectifs de performance' },
          { key: 'canCreatePerformanceObjectives', label: 'Créer des objectifs', dependsOn: 'canViewPerformanceObjectives' },
          { key: 'canUpdatePerformanceObjectives', label: 'Modifier des objectifs', dependsOn: 'canViewPerformanceObjectives' },
          { key: 'canViewAllPerformanceObjectives', label: 'Voir les objectifs de tout le monde', dependsOn: 'canViewPerformanceObjectives' },
          { key: 'canValidatePerformanceObjectives', label: 'Valider les objectifs (déclenche la prime)', dependsOn: 'canViewPerformanceObjectives' },
        ],
      },
    ],
  },
  {
    sectionId: 'activityReports',
    sectionTitle: '📋 Rapports d’activité',
    featureKey: 'activityReports',
    description: 'Suivi de ce que font les employés',
    groups: [
      {
        title: 'Rapports d’activité',
        featureKey: 'activityReports',
        permissions: [
          { key: 'canViewActivityReports', label: 'Voir les rapports d’activité' },
          { key: 'canCreateActivityReports', label: 'Créer / valider son rapport', dependsOn: 'canViewActivityReports' },
          { key: 'canViewAllActivityReports', label: 'Voir tous les rapports (équipe / business)', dependsOn: 'canViewActivityReports' },
        ],
      },
      {
        title: 'Types d’activité',
        featureKey: 'activityTypes',
        description: 'Paramètres → Types d’activité',
        permissions: [
          { key: 'canViewActivityTypes', label: 'Voir les types d’activité' },
          { key: 'canCreateActivityTypes', label: 'Créer des types d’activité', dependsOn: 'canViewActivityTypes' },
          { key: 'canUpdateActivityTypes', label: 'Modifier des types d’activité', dependsOn: 'canViewActivityTypes' },
          { key: 'canDeleteActivityTypes', label: 'Supprimer des types d’activité', dependsOn: 'canViewActivityTypes' },
        ],
      },
    ],
  },
  {
    sectionId: 'opportunities',
    sectionTitle: '🎯 Opportunités',
    featureKey: 'opportunities',
    description: 'Pipeline commercial — lié automatiquement à la configuration des pipelines',
    groups: [
      {
        title: 'Opportunités',
        featureKey: 'opportunities',
        permissions: [
          { key: 'canViewSalesPipeline', label: 'Voir la page Opportunités' },
          { key: 'canCreateOpportunities', label: 'Créer des opportunités', dependsOn: 'canViewSalesPipeline' },
          { key: 'canUpdateOpportunities', label: 'Modifier des opportunités', dependsOn: 'canViewSalesPipeline' },
          { key: 'canDeleteOpportunities', label: 'Supprimer des opportunités', dependsOn: 'canViewSalesPipeline' },
        ],
      },
      {
        title: 'Types de clients',
        featureKey: 'clientTypes',
        description: 'Paramètres → Types de clients',
        permissions: [
          { key: 'canViewClientTypes', label: 'Voir les types de clients' },
          { key: 'canCreateClientTypes', label: 'Créer des types de clients', dependsOn: 'canViewClientTypes' },
          { key: 'canUpdateClientTypes', label: 'Modifier des types de clients', dependsOn: 'canViewClientTypes' },
          { key: 'canDeleteClientTypes', label: 'Supprimer des types de clients', dependsOn: 'canViewClientTypes' },
        ],
      },
      {
        title: 'Catégories d’opportunités',
        featureKey: 'opportunityCategories',
        description: 'Paramètres → Catégories d’opportunités',
        permissions: [
          { key: 'canViewOpportunityCategories', label: 'Voir les catégories d’opportunités' },
          {
            key: 'canCreateOpportunityCategories',
            label: 'Créer des catégories d’opportunités',
            dependsOn: 'canViewOpportunityCategories',
          },
          {
            key: 'canUpdateOpportunityCategories',
            label: 'Modifier des catégories d’opportunités',
            dependsOn: 'canViewOpportunityCategories',
          },
          {
            key: 'canDeleteOpportunityCategories',
            label: 'Supprimer des catégories d’opportunités',
            dependsOn: 'canViewOpportunityCategories',
          },
        ],
      },
    ],
  },
  {
    sectionId: 'settings',
    sectionTitle: '⚙️ Paramètres',
    description: 'Configurations rares — accessibles via le menu avatar → Paramètres',
    groups: [
      {
        title: 'Facturation',
        description: 'Format des documents de vente, logo, cachet et signature',
        featureKey: 'pos',
        permissions: [
          { key: 'canViewInvoicing', label: 'Voir les paramètres de facturation' },
          {
            key: 'canUpdateInvoicing',
            label: 'Modifier la facturation (format, logo, cachet)',
            dependsOn: 'canViewInvoicing',
          },
        ],
      },
      {
        title: 'Profils de rôle',
        featureKey: 'employeesPayroll',
        permissions: [
          { key: 'canViewRoleProfiles', label: 'Voir les profils de rôle' },
          { key: 'canCreateRoleProfiles', label: 'Créer des profils de rôle', dependsOn: 'canViewRoleProfiles' },
          { key: 'canUpdateRoleProfiles', label: 'Modifier des profils de rôle', dependsOn: 'canViewRoleProfiles' },
          { key: 'canDeleteRoleProfiles', label: 'Supprimer des profils de rôle', dependsOn: 'canViewRoleProfiles' },
        ],
      },
      {
        title: 'Configuration pipeline',
        featureKey: 'pipelineConfig',
        description: 'Paramètres → Pipelines (toujours lié à la feature Opportunités)',
        permissions: [
          { key: 'canViewPipelines', label: 'Voir la configuration des pipelines' },
          { key: 'canCreatePipelines', label: 'Créer un pipeline', dependsOn: 'canViewPipelines' },
          { key: 'canUpdatePipelines', label: 'Modifier un pipeline', dependsOn: 'canViewPipelines' },
          { key: 'canDeletePipelines', label: 'Supprimer un pipeline', dependsOn: 'canViewPipelines' },
        ],
      },
    ],
  },
  {
    sectionId: 'licencePayment',
    sectionTitle: '💳 Règlements Nauvatek',
    featureKey: 'licencePayment',
    groups: [
      {
        title: 'Règlements & échéances',
        description: 'Licence, frais et échéances (page avatar → Règlements)',
        featureKey: 'licencePayment',
        permissions: [
          { key: 'canViewLicencePayment', label: 'Voir les règlements' },
          { key: 'canPayLicence', label: 'Régler une échéance', dependsOn: 'canViewLicencePayment' },
        ],
      },
    ],
  },
  {
    sectionId: 'users',
    sectionTitle: 'Utilisateurs',
    featureKey: 'users',
    groups: [
      {
        title: 'Gestion des utilisateurs',
        featureKey: 'users',
        permissions: [{ key: 'canManageUsers', label: 'Gérer les utilisateurs' }],
      },
    ],
  },
];

// Export pour la page "Mon compte / Mes droits" (affichage des droits de l'utilisateur connecté)
export const PERMISSION_SECTIONS_FOR_DISPLAY = PERMISSION_SECTIONS;

// Liste plate de tous les groupes pour la logique (toggle, dépendances)
const ALL_GROUPS = PERMISSION_SECTIONS.flatMap((s) => s.groups);

function findPermissionLabel(key: keyof RolePermissions): string | undefined {
  for (const g of ALL_GROUPS) {
    const p = g.permissions.find((x) => x.key === key);
    if (p) return p.label;
  }
  return undefined;
}

interface RoleProfilePermissionsEditorProps {
  value?: RolePermissions;
  onChange?: (permissions: RolePermissions) => void;
  /** Business cible — obligatoire pour filtrer les features (données Firestore à jour). */
  business: Business | null;
}

const RoleProfilePermissionsEditor: React.FC<RoleProfilePermissionsEditorProps> = ({
  value,
  onChange,
  business,
}) => {
  const { t } = useAppTranslation(['settings']);
  const [permissions, setPermissions] = useState<RolePermissions>(
    normalizeRolePermissions(value)
  );

  const permissionLabel = (key: keyof RolePermissions, fallback: string) =>
    t(`roleProfiles.permissions.${key}`, { defaultValue: fallback });

  const sectionTitleLabel = (section: PermissionSection) =>
    t(`roleProfiles.sections.${section.sectionId}.title`, { defaultValue: section.sectionTitle });

  const sectionDescriptionLabel = (section: PermissionSection) => {
    if (!section.description) return undefined;
    return t(`roleProfiles.sections.${section.sectionId}.description`, {
      defaultValue: section.description,
    });
  };

  const groupTitleLabel = (group: PermissionGroup) =>
    group.groupId
      ? t(`roleProfiles.groups.${group.groupId}.title`, { defaultValue: group.title })
      : group.title;

  const groupDescriptionLabel = (group: PermissionGroup) => {
    if (!group.description) return undefined;
    return group.groupId
      ? t(`roleProfiles.groups.${group.groupId}.description`, {
          defaultValue: group.description,
        })
      : group.description;
  };

  useEffect(() => {
    setPermissions(normalizeRolePermissions(value));
  }, [value]);

  const featureFilteredSections = useMemo(() => {
    if (!business) return [];

    return PERMISSION_SECTIONS.map((section) => {
      const filteredGroups = section.groups.filter((group) => {
        const gating = getPermissionGroupGatingFeatures(section, group);
        // Chaque bloc doit être conditionné par au moins une feature, toutes actives.
        if (gating.length === 0) return false;
        return gating.every((featureKey) => isFeatureEnabled(business, featureKey));
      });
      return {
        ...section,
        groups: filteredGroups,
      };
    }).filter((section) => section.groups.length > 0);
  }, [business]);

  const allPermissionKeys = useMemo(
    () =>
      featureFilteredSections
        .flatMap((s) => s.groups)
        .flatMap((g) => g.permissions)
        .map((p) => p.key),
    [featureFilteredSections],
  );
  const checkedCount = allPermissionKeys.filter((k) => permissions[k] === true).length;
  const isAllChecked = allPermissionKeys.length > 0 && checkedCount === allPermissionKeys.length;
  const isAllIndeterminate = checkedCount > 0 && checkedCount < allPermissionKeys.length;

  const handlePermissionChange = (key: keyof RolePermissions, checked: boolean) => {
    const newPermissions = { ...permissions, [key]: checked };

    if (!checked) {
      ALL_GROUPS.forEach((group) => {
        group.permissions.forEach((perm) => {
          if (perm.dependsOn === key) {
            newPermissions[perm.key] = false;
          }
        });
      });
    }

    const permission = ALL_GROUPS.flatMap((g) => g.permissions).find((p) => p.key === key);
    if (checked && permission?.dependsOn) {
      newPermissions[permission.dependsOn] = true;
    }

    setPermissions(newPermissions);
    onChange?.(newPermissions);
  };

  const handleGroupToggle = (group: PermissionGroup, checked: boolean) => {
    const newPermissions = { ...permissions };
    group.permissions.forEach((perm) => {
      newPermissions[perm.key] = checked;
    });
    setPermissions(newPermissions);
    onChange?.(newPermissions);
  };

  const handleToggleAll = (checked: boolean) => {
    const newPermissions = { ...permissions } as RolePermissions;
    allPermissionKeys.forEach((key) => {
      newPermissions[key] = checked;
    });
    setPermissions(newPermissions);
    onChange?.(newPermissions);
  };

  const isGroupAllChecked = (group: PermissionGroup): boolean =>
    group.permissions.every((perm) => permissions[perm.key] === true);
  const isGroupIndeterminate = (group: PermissionGroup): boolean => {
    const n = group.permissions.filter((perm) => permissions[perm.key] === true).length;
    return n > 0 && n < group.permissions.length;
  };

  return (
    <div>
      <Title level={4}>{t('roleProfiles.editor.title')}</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
        {t('roleProfiles.editor.intro')}
      </Text>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Checkbox
          checked={isAllChecked}
          indeterminate={isAllIndeterminate}
          onChange={(e) => handleToggleAll(e.target.checked)}
        >
          <strong>{t('roleProfiles.editor.toggleAll')}</strong>
          <Text type="secondary" style={{ marginLeft: 8, fontWeight: 'normal' }}>
            {t('roleProfiles.editor.checkedCount', {
              checked: checkedCount,
              total: allPermissionKeys.length,
            })}
          </Text>
        </Checkbox>
      </Card>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {featureFilteredSections.map((section) => (
          <div key={section.sectionId}>
            <Divider orientation="left" orientationMargin={0}>
              <strong>{sectionTitleLabel(section)}</strong>
            </Divider>
            {sectionDescriptionLabel(section) && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                {sectionDescriptionLabel(section)}
              </Text>
            )}
            <Space direction="vertical" size="middle" style={{ width: '100%', paddingLeft: 8 }}>
              {section.groups.map((group) => (
                <Card
                  key={group.groupId || group.title}
                  size="small"
                  title={
                    <Space>
                      <Checkbox
                        checked={isGroupAllChecked(group)}
                        indeterminate={isGroupIndeterminate(group)}
                        onChange={(e) => handleGroupToggle(group, e.target.checked)}
                      >
                        <strong>{groupTitleLabel(group)}</strong>
                      </Checkbox>
                      {groupDescriptionLabel(group) && (
                        <Tooltip title={groupDescriptionLabel(group)}>
                          <InfoCircleOutlined style={{ color: '#999' }} />
                        </Tooltip>
                      )}
                    </Space>
                  }
                >
                  <Space direction="vertical" size="small" style={{ width: '100%', paddingLeft: 24 }}>
                    {group.permissions.map((perm) => {
                      const isDisabled = !!perm.dependsOn && !permissions[perm.dependsOn];
                      const depFallback = perm.dependsOn ? findPermissionLabel(perm.dependsOn) : undefined;
                      const depLabel =
                        perm.dependsOn && depFallback
                          ? permissionLabel(perm.dependsOn, depFallback)
                          : null;
                      return (
                        <div key={perm.key}>
                          <Checkbox
                            checked={permissions[perm.key] === true}
                            disabled={isDisabled}
                            onChange={(e) => handlePermissionChange(perm.key, e.target.checked)}
                          >
                            {permissionLabel(perm.key, perm.label)}
                            {perm.description && (
                              <Tooltip title={perm.description}>
                                <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                              </Tooltip>
                            )}
                            {isDisabled && depLabel && (
                              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                                {t('roleProfiles.editor.requires', { label: depLabel })}
                              </Text>
                            )}
                          </Checkbox>
                        </div>
                      );
                    })}
                  </Space>
                </Card>
              ))}
            </Space>
          </div>
        ))}
      </Space>
    </div>
  );
};

export default RoleProfilePermissionsEditor;
